import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead } from '../components/SEOHead';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { PROVIDERS_DATA } from '../config/providersData';
import { StickyPhoneBar } from '../components/StickyPhoneBar';
import { 
  Phone, 
  Zap,
  Home,
  TrendingDown,
  Settings,
  Leaf,
  Calculator,
  Clock,
  Shield,
  ChevronRight
} from 'lucide-react';

export const ComparateurGeneriquePage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const [randomProviders, setRandomProviders] = useState([]);
  
  const country = 'FR';
  const vertical = 'electricity';
  const specialist = getSpecialistMessage(country, vertical);
  const saveaPhone = getSaveaPhone(country, vertical, 'LP');
  const saveaPhoneTel = getSaveaPhoneTel(country, vertical, 'LP');

  // 4 logos principaux hero - EDF, Engie, Total + 1 random
  const [randomLogo, setRandomLogo] = useState(null);
  
  const fixedLogos = [
    { src: '/images/logos/FAzUgTB.png', alt: 'EDF' },
    { src: '/images/logos/FAzUZYv.png', alt: 'Engie' },
    { src: '/images/logos/FAzg1MF.png', alt: 'TotalEnergies' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Récupérer 10 fournisseurs FR NRJ aléatoires
    const frNrjProviders = Object.values(PROVIDERS_DATA).filter(
      p => p.country === 'FR' && p.vertical === 'electricity'
    );
    const shuffled = frNrjProviders.sort(() => 0.5 - Math.random());
    setRandomProviders(shuffled.slice(0, 10));
    
    // Un logo random pour le hero (pas EDF, Engie, Total)
    const otherProviders = frNrjProviders.filter(
      p => !['EDF', 'Engie', 'TotalEnergies'].includes(p.name)
    );
    const randomOne = otherProviders[Math.floor(Math.random() * otherProviders.length)];
    if (randomOne) {
      setRandomLogo({ src: randomOne.logo_url, alt: randomOne.name });
    }
  }, []);
  
  const heroLogos = randomLogo ? [...fixedLogos, randomLogo] : fixedLogos;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Comparateur Électricité France ☎️ Gratuit 2026 | EDF Engie TotalEnergies"
        description="☎️ Comparateur électricité GRATUIT 2026. Comparez EDF 0.1940€, Engie 0.1820€, TotalEnergies. Économisez jusqu'à 200€/an. Conseillers disponibles 7j/7."
        keywords="comparateur electricite, comparer fournisseur electricite, EDF Engie TotalEnergies, prix electricite 2026, changer fournisseur, economiser electricite France"
        type="comparator"
      />
      
      {/* Schema.org LocalBusiness + ItemList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "LocalBusiness",
            "name": "SAVEA - Comparateur Électricité France",
            "description": "Service gratuit de comparaison des offres d'électricité en France",
            "telephone": "+33970252402",
            "url": "https://savea.info/fr/fournisseur/comparer-fournisseurs",
            "areaServed": "France",
            "priceRange": "Gratuit",
            "openingHours": "Mo-Sa 08:00-21:00"
          },
          {
            "@type": "ItemList",
            "name": "Fournisseurs d'électricité en France",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "EDF", "description": "Tarif réglementé 0.1940€/kWh"},
              {"@type": "ListItem", "position": 2, "name": "Engie", "description": "Électricité verte 0.1820€/kWh"},
              {"@type": "ListItem", "position": 3, "name": "TotalEnergies", "description": "Offres compétitives"},
              {"@type": "ListItem", "position": 4, "name": "Vattenfall", "description": "Fournisseur européen"},
              {"@type": "ListItem", "position": 5, "name": "Eni", "description": "Offres gaz et électricité"}
            ]
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Comment changer de fournisseur d'électricité ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Changer de fournisseur est gratuit, sans coupure et sans intervention. Le nouveau fournisseur s'occupe de toutes les démarches."
                }
              },
              {
                "@type": "Question",
                "name": "Quel est le fournisseur d'électricité le moins cher en 2026 ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Les prix varient selon votre profil de consommation. Nos conseillers gratuits vous aident à trouver l'offre la plus adaptée."
                }
              }
            ]
          }
        ]
      })}} />
      
      {/* HERO */}
      <section className="pt-6 pb-10 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <div className="max-w-lg mx-auto px-4">
          
          {/* Badge */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-1.5">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-emerald-300 text-xs font-medium">Service gratuit et sans engagement</span>
            </div>
          </div>
          
          {/* Titre */}
          <h1 className="text-2xl md:text-3xl font-bold mb-3 text-center leading-tight text-white">
            Comparez gratuitement les <span className="text-emerald-400">offres d'électricité</span> et de gaz en France
          </h1>
          
          <p className="text-slate-300 text-sm text-center mb-6">
            Trouvez l'offre la plus adaptée à votre consommation parmi nos fournisseurs partenaires
          </p>
          
          {/* Logos en grille 2x2 mobile */}
          <div className="grid grid-cols-4 gap-2 mb-6 max-w-xs mx-auto">
            {heroLogos.map((logo, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-2 flex items-center justify-center aspect-square shadow-lg"
              >
                <img 
                  src={logo.src}
                  alt={logo.alt}
                  className="h-8 w-auto object-contain"
                  loading="eager"
                />
              </div>
            ))}
          </div>

          {/* CTA Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
            
            {/* Numéro principal */}
            <a 
              href={`tel:${saveaPhoneTel}`}
              className="w-full flex flex-col items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg py-4 rounded-xl shadow-lg shadow-emerald-500/30 transition-all duration-300 mb-4"
              data-testid="main-cta-phone"
            >
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5" />
                <span className="savea-phone-number">{saveaPhone}</span>
              </div>
              <span className="text-emerald-200 text-xs mt-1">Numéro Gratuit</span>
            </a>

            {/* Boutons secondaires */}
            <div className="grid grid-cols-2 gap-3">
              <a 
                href={`tel:${saveaPhoneTel}`}
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                data-testid="cta-souscrire"
              >
                <Zap className="w-4 h-4 text-yellow-400" />
                <span>Souscrire</span>
              </a>
              <a 
                href={`tel:${saveaPhoneTel}`}
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                data-testid="cta-demenagement"
              >
                <Home className="w-4 h-4 text-blue-400" />
                <span>Déménagement</span>
              </a>
            </div>

            {/* Horaires */}
            <div className="text-center mt-4 text-slate-300 text-xs">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Lundi - Samedi • 8h-21h
              </span>
            </div>
          </div>
          
        </div>
      </section>

      {/* Fournisseurs comparés - 10 random avec prix */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
            Comparez les tarifs des fournisseurs
          </h2>
          <p className="text-gray-500 text-sm text-center mb-6">
            Prix du kWh en option Base - Tarifs mars 2026
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {randomProviders.map((provider, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={provider.logo_url}
                      alt={provider.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{provider.name}</h3>
                    <p className="text-xs text-gray-500">
                      {provider.offers?.[0]?.green ? '🌱 Offre verte' : 'Offre classique'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-600">{provider.main_price}</div>
                  <div className="text-xs text-gray-400">TTC/kWh</div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA après liste */}
          <div className="text-center mt-8">
            <a 
              href={`tel:${saveaPhoneTel}`}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-full transition-all"
            >
              <Phone className="w-4 h-4" />
              <span>Comparer avec un conseiller</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Comprendre l'électricité en France */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Comprendre le marché de l'électricité en France
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Le marché de l'énergie */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Le marché ouvert à la concurrence</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Depuis 2007, le marché de l'électricité est ouvert à la concurrence en France. 
                  Vous pouvez librement choisir votre fournisseur parmi une trentaine d'acteurs : 
                  EDF (fournisseur historique), Engie, TotalEnergies, ou des fournisseurs alternatifs 
                  comme Ekwateur, Mint Énergie ou OHM Énergie.
                </p>
              </CardContent>
            </Card>

            {/* Économiser sur sa facture */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Comment économiser sur sa facture ?</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Le tarif réglementé (TRV) d'EDF est à <strong>0.1940€/kWh</strong> en mars 2026. 
                  Les fournisseurs alternatifs proposent des offres jusqu'à <strong>15% moins chères</strong>. 
                  En changeant de fournisseur, un foyer moyen peut économiser entre 100€ et 200€ par an 
                  sans changer ses habitudes de consommation.
                </p>
              </CardContent>
            </Card>

            {/* Changer de puissance */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Settings className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Optimiser sa puissance de compteur</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  La puissance de votre compteur (3, 6, 9, 12 kVA...) impacte directement votre abonnement. 
                  Beaucoup de foyers paient pour une puissance supérieure à leurs besoins. 
                  <strong> Passer de 9 à 6 kVA peut faire économiser jusqu'à 30€/an</strong> sur l'abonnement. 
                  Nos conseillers analysent votre consommation pour vous recommander la puissance adaptée.
                </p>
              </CardContent>
            </Card>

            {/* Heures creuses */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Option Base ou Heures Creuses ?</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  L'option <strong>Heures Creuses</strong> (généralement 22h-6h) permet de payer moins cher 
                  une partie de votre consommation. Elle est intéressante si vous consommez au moins 
                  <strong> 30% de votre électricité la nuit</strong> (chauffe-eau, lave-linge programmé...). 
                  Sinon, l'option Base avec un prix unique reste plus avantageuse.
                </p>
              </CardContent>
            </Card>

            {/* Énergie verte */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Qu'est-ce que l'électricité verte ?</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Une offre "verte" garantit que l'équivalent de votre consommation est injecté 
                  dans le réseau à partir de sources renouvelables (éolien, solaire, hydraulique). 
                  Le label <strong>VertVolt</strong> de l'ADEME distingue les offres les plus engagées. 
                  Aujourd'hui, les offres vertes sont souvent <strong>au même prix</strong> que les offres classiques.
                </p>
              </CardContent>
            </Card>

            {/* Changer de fournisseur */}
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Changer de fournisseur, c'est simple</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Changer de fournisseur est <strong>gratuit, sans coupure et sans intervention technique</strong>. 
                  Votre nouveau fournisseur s'occupe de toutes les démarches de résiliation. 
                  Vous n'êtes jamais engagé : vous pouvez changer à tout moment sans frais. 
                  Le réseau Enedis reste le même, seul le fournisseur qui vous facture change.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Déménagement */}
      <section className="py-10 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-emerald-200 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium mb-3">
                <Home className="w-3 h-3" />
                Déménagement
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Vous déménagez ? On s'occupe de tout.
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                Nos conseillers vous accompagnent dans toutes vos démarches : 
                résiliation de l'ancien contrat, mise en service au nouveau logement, 
                comparaison des offres pour votre nouvelle adresse. 
                Un seul appel suffit pour tout organiser.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                  Mise en service sous 24-48h
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                  Résiliation de l'ancien contrat gérée
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                  Comparaison gratuite des offres
                </li>
              </ul>
            </div>
            <div className="flex-shrink-0">
              <a 
                href={`tel:${saveaPhoneTel}`}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-all"
              >
                <Phone className="w-5 h-5" />
                <span className="savea-phone-number">{saveaPhone}</span>
              </a>
              <p className="text-center text-emerald-700 text-xs mt-2">Lun-Sam 8h-21h</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ rapide */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4">
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Combien de temps pour changer de fournisseur ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Le changement prend effet immédiatement ou à la date de votre choix. 
                Il n'y a aucune coupure d'électricité pendant le changement. 
                Comptez environ 10 minutes pour finaliser la souscription par téléphone.
              </p>
            </details>
            
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Est-ce que je vais payer des frais de résiliation ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Non, les contrats d'électricité pour particuliers sont sans engagement. 
                Vous pouvez changer de fournisseur à tout moment sans frais de résiliation.
              </p>
            </details>
            
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Quelle est la différence entre fournisseur et distributeur ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Le distributeur (Enedis) gère le réseau électrique et les compteurs. 
                Il reste le même quel que soit votre fournisseur. 
                Le fournisseur (EDF, Engie, TotalEnergies...) est celui qui vous vend l'électricité et vous facture.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 pb-20 bg-slate-900">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-3">
            Prêt à réduire votre facture ?
          </h2>
          <p className="text-slate-300 text-sm mb-6">
            Appelez-nous gratuitement pour une comparaison personnalisée
          </p>
          <a 
            href={`tel:${saveaPhoneTel}`}
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-all"
          >
            <Phone className="w-5 h-5" />
            <span className="savea-phone-number">{saveaPhone}</span>
          </a>
        </div>
      </section>

      {/* Sticky Phone Bar - Couleur verte comparateur */}
      <StickyPhoneBar providerColor="bg-emerald-600 hover:bg-emerald-700" />
    </div>
  );
};

export default ComparateurGeneriquePage;
